import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { ActionRegistryService } from '../../services/action-registry.service';
import { FaqRegistryService } from '../../services/faq-registry.service';
import { AiGuideChatService } from '../../services/ai-guide-chat.service';

/**
 * The AI chat sidebar — message list + input, mounted in the app shell.
 *
 * On submit it delegates to {@link AiGuideChatService}. Live walkthrough
 * progress + exit now live in the guide overlay callout and the bottom-center
 * control bar, so this component is just the chat surface.
 */
@Component({
  selector: 'app-ai-chat-sidebar',
  imports: [],
  templateUrl: './ai-chat-sidebar.component.html',
  styleUrl: './ai-chat-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AiChatSidebarComponent {
  private readonly chat = inject(AiGuideChatService);
  private readonly registry = inject(ActionRegistryService);
  private readonly faqRegistry = inject(FaqRegistryService);

  protected readonly messages = this.chat.messages;
  protected readonly busy = this.chat.busy;
  protected readonly featuredTasks = this.registry.featuredTasks;
  protected readonly featuredFaqsByCategory = this.faqRegistry.featuredFaqsByCategory;

  protected readonly draft = signal('');

  private readonly thread = viewChild<ElementRef<HTMLElement>>('thread');

  constructor() {
    // Keep the thread scrolled to the newest message (also during streaming).
    effect(() => {
      this.messages();
      const el = this.thread()?.nativeElement;
      if (el) {
        queueMicrotask(() => (el.scrollTop = el.scrollHeight));
      }
    });
  }

  protected onInput(event: Event): void {
    this.draft.set((event.target as HTMLTextAreaElement).value);
  }

  protected send(): void {
    const text = this.draft().trim();
    if (!text || this.busy()) {
      return;
    }
    void this.chat.handleUserMessage(text);
    this.draft.set('');
  }

  protected startFeaturedTask(taskId: string): void {
    this.chat.startFeaturedTask(taskId);
  }

  protected answerFeaturedFaq(faqId: string): void {
    this.chat.answerFeaturedFaq(faqId);
  }

  protected resetChat(): void {
    this.chat.clear();
    this.draft.set('');
  }

  protected close(): void {
    this.chat.setAiGuideOpen(false);
  }
}
